import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Loader2, Package, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useCart } from '../context/CartContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const OrderConfirmationPage = () => {
  const [searchParams] = useSearchParams();
  const { clearCart } = useCart();
  const [status, setStatus] = useState('loading');
  const [orderNumber, setOrderNumber] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const sessionId = searchParams.get('session_id');
  const directOrderNumber = searchParams.get('order_number');

  useEffect(() => {
    if (directOrderNumber) {
      setOrderNumber(directOrderNumber);
      setStatus('success');
      clearCart();
      return;
    }

    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId, directOrderNumber]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setOrderNumber(response.data.order_number);
        clearCart();
        return;
      } else if (response.data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6] pt-28 pb-16" data-testid="order-confirmation-page">
      <div className="max-w-2xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Card className="border-[#E3DBD1] bg-white rounded-sm text-center">
            <CardContent className="py-12 px-8">
              {status === 'loading' && (
                <div data-testid="payment-loading">
                  <Loader2 className="w-16 h-16 text-[#7B3E19] mx-auto mb-6 animate-spin" />
                  <h1 className="text-2xl font-heading font-semibold text-[#2A1A14] mb-2">
                    Processing Payment...
                  </h1>
                  <p className="text-[#786C65]">
                    Please wait while we confirm your payment.
                  </p>
                </div>
              )}

              {status === 'success' && (
                <div data-testid="payment-success">
                  <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
                  <h1 className="text-3xl font-heading font-semibold text-[#2A1A14] mb-4">
                    Thank You for Your Order!
                  </h1>
                  <p className="text-[#786C65] mb-6">
                    Your payment was successful. We're preparing your delicious Sicilian Chocolate Spice Cookies.
                  </p>
                  
                  {orderNumber && (
                    <div className="bg-[#EAE3D9] rounded-sm p-6 mb-8">
                      <p className="text-sm text-[#786C65] mb-2">Order Number</p>
                      <p className="text-2xl font-heading font-semibold text-[#7B3E19]" data-testid="order-number">
                        {orderNumber}
                      </p>
                      <p className="text-sm text-[#786C65] mt-2">
                        Save this number to track your order
                      </p>
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Link to="/track-order">
                      <Button className="bg-[#7B3E19] hover:bg-[#633113] text-white rounded-sm px-6">
                        <Package className="w-4 h-4 mr-2" />
                        Track Order
                      </Button>
                    </Link>
                    <Link to="/">
                      <Button variant="outline" className="border-[#7B3E19] text-[#7B3E19] hover:bg-[#7B3E19] hover:text-white rounded-sm px-6">
                        Continue Shopping
                      </Button>
                    </Link>
                  </div>
                </div>
              )}

              {(status === 'expired' || status === 'timeout') && (
                <div data-testid="payment-failed">
                  <XCircle className="w-20 h-20 text-red-500 mx-auto mb-6" />
                  <h1 className="text-2xl font-heading font-semibold text-[#2A1A14] mb-4">
                    {status === 'expired' ? 'Payment Session Expired' : 'Payment Verification Timeout'}
                  </h1>
                  <p className="text-[#786C65] mb-6">
                    {status === 'expired' 
                      ? 'Your payment session has expired. Please try again.'
                      : 'We couldn\'t verify your payment. Please check your email for confirmation or contact support.'
                    }
                  </p>
                  <Link to="/checkout">
                    <Button className="bg-[#7B3E19] hover:bg-[#633113] text-white rounded-sm px-6">
                      Try Again
                    </Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};
